import type { ReactNode } from 'react'

import { useMe } from '../../api/hooks'
import { CONSENT_TEXT_VERSION } from '../../copy/firstRun'
import { AiConsentAsk } from './AiConsentAsk'
import { nextConsentAsk } from './consentRule'

/**
 * Puts the AI ask in place of the app, once, for a person who finished first run without being asked
 * (#399, CS-D5, CO-D1): an account from before the consents existed, or a first run cut short between the
 * summary and "Ready to go". The rule is `nextConsentAsk`: never asked. Someone who answered, whichever way
 * and to whichever version of the text, is never stopped here; their way back is Settings › Privacy.
 *
 * It stands where `PendingDeletionGate` stands, inside it, around the shell (`Layout`), so none of the
 * shell's requests is made while the ask shows, and an account pending deletion meets its own screen
 * first. First run is outside the shell and asks for itself (`FirstRun`'s own step), so this gate
 * never doubles it.
 *
 * **It cannot be dismissed from inside**, but it is no dead end: its two buttons both answer, both
 * store the answer, and the gate gives way the moment `/api/me` says the answer was stored
 * (`useAnswerConsent` puts the server's reply into the `['me']` data). A failed save is said in place
 * and leaves the buttons.
 *
 * **Unknown is not asked.** While `/api/me` has not answered, or failed, or carries no consents, the
 * children render, as `PendingDeletionGate` does and for the same reason (`Layout`'s own doc): holding
 * the shell back for a request answered in a few milliseconds would cost every ordinary cold load a
 * blank screen. The price is paid by the person who must be asked: the shell may draw for one round
 * trip before this replaces it. Nothing the shell asks for is refused for want of a consent yet, and
 * `RequireFirstRun` inside it draws nothing routed until `/api/me` has answered.
 *
 * **A person who has not finished first run is not asked here.** `RequireFirstRun` sends them to
 * first run, which asks after its summary.
 */
export function ConsentGate({ children }: { children: ReactNode }) {
  const { data } = useMe()
  const ask = data?.first_run_complete
    ? nextConsentAsk(data.consents?.ai_processing, CONSENT_TEXT_VERSION.aiProcessing)
    : null
  if (!ask) return <>{children}</>
  return <AiConsentAsk variant={ask} />
}
